import { IRowNode } from '@ag-grid-community/core';
import { BaseApiResponseT } from '@common/types';
import type { PendingApprovals } from '@store/Global/types';

export type FlagT = {
  flagText: string;
  flagType: string;
  currentState?: string;
  id: number;
  loadId: number;
};

export interface LoadStops {
  timeZoneOffset: number;
  stopType: string;
  id: number;
  customerId: number;
  loadId: number;
  stopDateTime: string;
  stopDateTimeFormatted: string;
  description: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  locationName: string | null;
  contact: string | null;
  phone: string | null;
  pickUp: boolean;
  dropOff: boolean;
  stopOrder: number;
  origin: boolean;
  destination: boolean;
  createdDate: string;
  modifiedDate: string;
  referenceNum: string | null;
  time: string | null;
  instructions: string | null;
  directions: string | null;
  needsAppointment: boolean | null;
  appointmentDate: string | null;
  driverInTime: string | null;
  driverOutTime: string | null;
  appointmentNum: string | null;
  email: string | null;
  stopCode: string | null;
  countryCode: string | null;
  arrived: boolean;
  lat: number | null;
  long: number | null;
  practicalMileageToNextStop: number | null;
  shortestMileageToNextStop: number | null;
}

export enum AiTrackingStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export interface ILoadBoardViewModel {
  aiTrackingStatusLocalized: AiTrackingStatus | null;
  checkCallStatus?: string | null;
  stops: LoadStops[];
  loadTrackingLatitude: number | null;
  loadTrackingLongitude: number | null;
  hasELDConnected: boolean;
  carrierId: number;
  trackingStatus: string | null;
  loadId: number;
  orderId: number;
  customerName: string;
  customerId: number;
  referenceNumber?: string | null;
  carrierName: string;
  status: string;
  office: string;
  officeId: number;
  owner: string;
  ownerId: number;
  assignedTo: string;
  accountManager: string;
  assignedToGroupId: number | null;
  salesManager: string;
  coveredBy: string;
  coveredById: number;
  origin?: string;
  originCity?: string;
  originState?: string;
  originCountry?: string;
  mode: string;
  destination: string;
  destinationCity?: string;
  destinationState?: string;
  destinationCountry?: string;
  pickUps: number;
  pickDate: Date;
  pickUpTime?: Date | null;
  dropOffs: number;
  dropDate: Date;
  dropOffTime?: Date | null;
  equipment: 'F' | 'R'; // Other?
  flagId?: number | null;
  flagText?: string | null;
  flagType?: string | null; // ?
  currentState?: string | null;
  assignedGroupId?: number | null;
  product?: string | null;
  claim: boolean;
  lastCheckCallDate?: Date | null;
  lastCheckCallComment?: string | null;
  sharedWithOfficeId?: Date | null;
  confirmationSigned: boolean;
  postingId?: number | null;
  miles: number;
  assignedToId: number;
  customerGroups?: string;
  salesManagerId?: number;
  accountManagerId?: number;
  loadNumber: string;
  sharedCapacity?: boolean;
  trackingNumber: string;
  proNumber: string;
  stopPickup: string;
  stopDropoff: string;
  my: string;
  myCustomers: string;
  stopsRatio: string;
  nextCheckCallDateUtc?: Date | null;
  pendingBids: number;
  totalBids: number;
  matchOriginDistance: number;
  matchDestinationDistance: number;
  weightKg: number | null;
  weightLbs: number | null;
  numStops: number;
  hazmatRequired: boolean;
  tarpRequired: boolean;
  teamRequired: boolean;
  overDimension: boolean;
  rampsRequired: boolean;
  maxPrice: number | null;
  minPrice: number | null;
  driverPhone: string | null;
  cargoValue: number | null;
  loadCreatedDateUtc: string | null;
  hasTracking: boolean;
  operationsRep?: string | null;
  originTimeMilitary: string | undefined;
  pickUpTimeMilitarySortKey: string;
  destinationTimeMilitary: string | undefined;
  dropOffTimeMilitarySortKey: string;
  orderFlags: FlagT[];
  customerTier: string | null;
  capacityOpsRep?: string | null;
  capacityOpsRepId?: number | null;
  dropTrailer?: boolean;
}

export type LoadBoardMetaDataT = {
  loadId: number;
  ownerId: number;
  customerId: number;
  coveredById: number;
  assignedToGroupId: number | undefined;
  assignedToId: number | undefined;
  capacityOpsRepId: number | undefined;
  loadBoardLine?: ILoadBoardViewModel;
};

export type LoadBoardApiResponseT = {
  isEnabled: boolean;
  pendingApprovals: PendingApprovals[];
  message: string | null;
  items: ILoadBoardViewModel[];
  totalCount: number;
  page: number;
  pageSize: number;
  postFilterCount: number;
  preFilterCount: number;
  membersByGroupName: Record<string, string[] | undefined>;
  officeGroupIdsByMachingNames: Record<string, number | undefined>;
  groupIds: number[];
};

export type LoadBoardState = {
  items: ILoadBoardViewModel[];
  filteredItems: ILoadBoardViewModel[];
  savedSearches: SavedSearchT[];
  filters: SavedSearchT[];
  cache: SavedSearchT | undefined;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  updateLock: boolean;
  overlay: boolean;
  membersByGroupName: Record<string, string[] | undefined>;
  officeGroupIdsByMachingNames: Record<string, number | undefined>;
  onlyEmpty: boolean;
  myItems: boolean;
  tabStatus: string | null;
  groupIds: number[];
};

export enum SavedSearchCategory {
  Legacy = 'Loadboard',
  Advanced = 'LoadboardVNEXTAdvanced',
  Basic = 'LoadboardVNEXTBasic',
  Cache = 'LoadboardCache',
  MobileCache = 'LoadboardMobileCache',
  Layout = 'Layout',
  TrackTrace = 'TrackTrace',
  BillingQueue = 'BillingQueue',
  Customers = 'Customers',
  CreditApplications = 'CreditApplications',
  RevenueSearch = 'RevenueSearch',
  SidePanel = 'SidePanel',
}

export type SavedSearchT = {
  id: number;
  userId: number;
  category: SavedSearchCategory;
  name: string;
  params: string;
  type: string;
  createdDate: string;
  createdBy: string;
};

export type SavedSearchApiResponseT = BaseApiResponseT<SavedSearchT[]>;
export type SaveSearchSettingsApiResponseT = BaseApiResponseT<SavedSearchT>;

export type PostLoadT = {
  Comment: string;
  LoadId: number;
};

export type BulkSharedCapacityRequest = {
  loadId: number;
  orderId: number;
  removeCapacity: false;
};

export type BulkSharedCapacityApiError = {
  code: string;
  message: string;
};

export type BulkSharedCapacityEligibilityResult = {
  eligibleLoadIds: number[];
  failedLoadIds: number[];
  errors: BulkSharedCapacityApiError[];
};

export type BulkSharedCapacityItemResult = {
  assignedId: number | null;
  assignedName: string | null;
  maxBuyRate: number | null;
  capacityGroupName: string | null;
};

export type BulkSharedCapacityResult = {
  success: boolean;
  partialSuccess: boolean;
  failedLoadIds: number[];
  errors: BulkSharedCapacityApiError[];
  results: BulkSharedCapacityItemResult[];
};

export type PostMultipleResponseT = {
  errors: { error: string; message: string }[] | null;
  partialSuccess: boolean;
  failedPostingIds: number[];
  success: boolean;
  postings: ILoadBoardViewModel[];
};

export type UnPostMultipleResponseT = {
  errors: { error: string; message: string }[] | null;
  partialSuccess: boolean;
  success: boolean;
  postings: number[];
};

export type ValuesOf<T extends Readonly<string[]>> = T[number];

export type FilterPassT = {
  fn: (n: IRowNode) => boolean;
  type: 'group' | 'customer' | undefined;
  filter: string;
};

export type LoadboardFetchOptsT = {
  status?: string[];
  restrictToMyUserId?: number | null;
  startDate?: Date | null;
  endDate?: Date | null;
  restoreMyItems?: boolean;
};

export type DateFilterOption = {
  value: string;
  label: string;
  getDateRange: () => { start: Date | null; end: Date | null };
};
